import {
	Autocomplete,
	AutocompleteRenderInputParams,
	Checkbox,
	styled,
	Chip,
	Box,
	TextField,
	Typography,
	Stack,
	InputAdornment,
	IconButton,
	createFilterOptions,
} from '@mui/material';

import React, { useEffect, useState } from 'react';

import { ReactComponent as DownArrow } from 'common/assets/images/down-arrow-dropdown.svg';
import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';

import styles from './AutoComplete.module.scss';

const {
	whiteBackground,
	chipBackground,
	borderColor,
	arrowColor,
	labelTextColor,
	labelTextSize,
	labelTextWeight,
} = styles;

interface IAutoCompleteProps {
	field?: object;
	data: any[];
	type?: string;
	selectedData?: any[];
	displayLabel: string;
	viewChange?: boolean;
	onUserSelect: (data: any[]) => void;
	showLabel?: boolean;
	apiSearch?: boolean;
	onTextChange?: (data: string) => void;
	tagsCount?: number;
	isSearchLoading?: boolean;
	allowAdd?: boolean;
	disabled?: boolean;
}

const StyledTextField = styled(TextField)({
	'& label, & label.Mui-focused': {
		color: labelTextColor,
		fontSize: labelTextSize,
		fontWeight: labelTextWeight,
		marginTop: '9px',
	},
});

const filter = createFilterOptions();

export const AutoComplete = (props: IAutoCompleteProps) => {
	const {
		data,
		selectedData,
		onUserSelect,
		displayLabel,
		viewChange,
		showLabel,
		apiSearch,
		onTextChange,
		tagsCount,
		isSearchLoading,
		allowAdd,
		disabled = false,
	} = props;
	const {
		whiteText,
		checkboxcheckedcolor,
		checkboxuncheckedcolor,
		listHoverColor,
	} = styles;
	const allTags: any[] = data;
	const [, setSelectedIds] = useState<string[]>([]);
	const [selectedInfo, setSelectedInfo] = useState<any[]>([]);
	const limitTags = tagsCount || 3;

	const [nextCount, setNextCount] = useState<number>(limitTags);
	const [showMoreSelected, setShowMore] = useState<boolean>(false);

	const showMoreAction = (numTags: number, limitTags: number, value: any) =>
		!showMoreSelected ? (
			<Typography component="span" className={styles['show-more-text']}>
				...&nbsp;+{numTags - limitTags}&nbsp;more
				<IconButton
					data-testid="showMoreBtn"
					onClick={() => {
						setNextCount(value.length);
						setShowMore(true);
					}}
				>
					<DownArrow
						fill={arrowColor}
						className={styles['roles-icon-img-show']}
					/>
				</IconButton>
			</Typography>
		) : (
			<Typography
				component="span"
				className={styles['show-more-text']}
				data-testid="showLess"
			>
				&nbsp;...&nbsp;Show less
				<IconButton
					onClick={() => {
						setNextCount(limitTags);
						setShowMore(false);
					}}
				>
					<DownArrow
						fill={arrowColor}
						className={styles['roles-icon-img-show-down']}
					/>
				</IconButton>
			</Typography>
		);

	const commonInputProps = {
		type: 'search',
		tabIndex: 3,
	};

	useEffect(() => {
		let selectedIDS;
		if (selectedData && selectedData.length > 0) {
			setSelectedInfo(selectedData);
			selectedIDS = selectedData?.map((ele: any) => ele?.id);
		} else {
			setSelectedInfo([]);
			selectedIDS = [];
		}
		setSelectedIds(selectedIDS);
	}, [selectedData]);
	return (
		<Autocomplete
			disabled={disabled}
			id="multiple-limit-tags"
			componentsProps={{
				paper: {
					sx: {
						width: '100%',
						'& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"]':
							{
								background: `${whiteBackground} !important`,
							},
						'& .MuiAutocomplete-option': {
							backgroundColor: `${whiteBackground} !important`,
							background: `${whiteBackground} !important`,
						},
						'& .MuiAutocomplete-listbox .MuiAutocomplete-option:hover': {
							backgroundColor: `${listHoverColor} !important`,
							background: `${listHoverColor} !important`,
						},
						'& .MuiAutocomplete-listbox .MuiAutocomplete-option:active': {
							backgroundColor: whiteBackground,
						},
						"& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected ='false'] ":
							{
								backgroundColor: whiteBackground,
							},
					},
				},
			}}
			data-testid="user-mail"
			multiple
			size="small"
			className={styles['search-field-size']}
			tabIndex={-1}
			sx={{
				width: { sm: '100%', md: '100%' },
				outline: 0,

				'& .MuiFormControl-root .MuiTextField-root': {
					border: `1px solid ${borderColor} !important`,
					borderRadius: '4px',
				},
				'&.MuiAutocomplete-root .MuiOutlinedInput-root': {
					paddingTop: '10px',
					paddingBottom: '10px',
					paddingLeft: '6px',
					border: `1px solid ${borderColor} !important`,
					borderRadius: '5px',
				},

				'& .MuiOutlinedInput-root': {
					backgroundColor: whiteBackground,
				},
				'& .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
					{
						padding: '7px 4px 7px 8px',
					},
				'& + .MuiAutocomplete-popper .MuiAutocomplete-option': {
					backgroundColor: whiteBackground,
					borderBottom: `1px solid ${borderColor}`,
				},
				"& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected='true']":
					{
						backgroundColor: whiteBackground,
						paddingTop: '5px !important',
						paddingBottom: '5px !important',
					},
				"& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected='false']":
					{
						backgroundColor: whiteBackground,
						paddingTop: '5px !important',
						paddingBottom: '5px !important',
					},

				"& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected='true']:hover":
					{
						backgroundColor: whiteBackground,
						background: whiteBackground,
						paddingTop: '5px !important',
						paddingBottom: '5px !important',
					},

				"& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected ='true'] .Mui-focused":
					{
						backgroundColor: 'red',
						background: whiteBackground,
					},
				"& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected ='true'] .Mui-Selected":
					{
						backgroundColor: 'red',
						background: whiteBackground,
					},
			}}
			options={allTags}
			loading={isSearchLoading}
			value={selectedInfo}
			filterOptions={(options, params) => {
				const filtered = filter(options, params);
				if (params.inputValue !== '' && allowAdd) {
					filtered.push({
						isAdded: true,
						name: params.inputValue,
						label: `Add "${params.inputValue}"`,
						displayLabel: `Add "${params.inputValue}"`,
					});
				}
				return filtered;
			}}
			getOptionLabel={(option: any) => option?.displayLabel}
			disableCloseOnSelect
			popupIcon={
				!showLabel && (
					<DownArrow fill={arrowColor} className={styles['roles-icon-img']} />
				)
			}
			renderTags={(value, getTagProps) => {
				const numTags = value.length;

				return (
					<>
						{!showLabel &&
							value &&
							value.length > 0 &&
							value.slice(0, nextCount).map((option, index) => (
								<Chip
									variant="filled"
									sx={{
										color: whiteText,
										borderRadius: 8,
										height: 25,
										backgroundColor: chipBackground,
										'&:active': {
											backgroundColor: chipBackground,
										},
										'&:hover': {
											cursor: 'pointer',
											backgroundColor: chipBackground,
										},
									}}
									label={option?.displayLabel || ''}
									deleteIcon={
										<DrawerClose
											fill={whiteText}
											className={styles['chips-delete-icon']}
										/>
									}
									{...getTagProps({ index })}
								/>
							))}
						{!showLabel && (
							<span>
								{numTags > limitTags
									? showMoreAction(numTags, limitTags, value)
									: null}
							</span>
						)}
					</>
				);
			}}
			renderOption={(props: any, option: any, { selected }) => (
				<div
					{...props}
					key={`listItem-${option.label}-${option.id}-${selected}`}
				>
					{option && (
						<Box className={styles['roles-maillist']} pl={0}>
							<Checkbox
								disableRipple
								disabled={option?.isExisting}
								key={`item-${option.label}-${option.id}`}
								sx={{
									color: checkboxuncheckedcolor,
									cursor: option?.isExisting
										? 'not-allowed !important'
										: 'pointer',
									'&.Mui-checked': { color: checkboxcheckedcolor },
								}}
								checked={selected || option?.isExisting}
							/>
							{!viewChange ? (
								<Stack direction="row">
									<Typography className={styles['roles-maillist-name']}>
										{option?.firstName} {option?.lastName}
									</Typography>
									<Typography className={styles['roles-maillist-seperator']}>
										|
									</Typography>
									<Typography className={styles['roles-maillist-mail']}>
										{option?.email}
									</Typography>
								</Stack>
							) : (
								<Typography>{option?.label}</Typography>
							)}
						</Box>
					)}
				</div>
			)}
			renderInput={(params: AutocompleteRenderInputParams) => (
				<StyledTextField
					key="mail"
					{...params}
					label={!showLabel && displayLabel}
					placeholder={showLabel ? displayLabel : ''}
					sx={{
						'& .MuiOutlinedInput-root': {
							'& fieldset': {
								border: 'none',
								borderRadius: '1px',
							},
							'&.Mui-focused fieldset': {
								border: 'none',
								borderRadius: '1px',
							},
						},
					}}
					InputProps={
						showLabel
							? {
									...params.InputProps,
									...commonInputProps,

									startAdornment: (
										<InputAdornment position="start">
											<SearchOutlined className={styles['input-search-icon']} />
										</InputAdornment>
									),
							  }
							: {
									...params.InputProps,
									...commonInputProps,
							  }
					}
				/>
			)}
			onChange={(_: any, data: any) => {
				setSelectedInfo(data);
				onUserSelect(data);
			}}
			onInputChange={(event: any) => {
				if (apiSearch && onTextChange) {
					onTextChange(event?.target?.value);
				}
			}}
			isOptionEqualToValue={(option: any, value: any) =>
				value === undefined || value === '' || option?.id === value?.id
			}
		/>
	);
};
